import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import { connect } from 'react-redux';
import { Cookie } from '@cotyorg/ccx-organism-cookie';
import { HeaderTwoRow } from '@cotyorg/ccx-organism-header';
import { FooterTwoRow } from '@cotyorg/ccx-organism-footer';
import { Social } from '@cotyorg/ccx-molecules/Social';
import { Newsletter } from '@cotyorg/ccx-molecules/Newsletter';
import Search from '@cotyorg/ccx-organism-search/components/Search';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import MikMakScript from '@cotyorg/ccx-molecules/MikMakScript';
import { trackPageView } from '@cotyorg/ccx-utils/js/tracking/google-analytics/events/page';
import readJSONFromScriptTag from '@cotyorg/ccx-utils/js/helpers/read-json-from-script-tag';
import { useMikMakAdobeTracking } from '@cotyorg/ccx-utils/js/hooks/useMikMakAdobeTracking';
import { useMikMakGaTracking } from '@cotyorg/ccx-utils/js/hooks/useMikMakGaTracking';
import { BasketModalComponent } from '../../molecules/basket';
import {
  showModal as showModalAction,
  hideModal as hideModalAction,
  getBasketQuantity,
} from '../../molecules/basket/modules';
import { createWebPageSchema } from '../../utils/helpers/schema';

const buildWebPageSchemaForState = (state) => {
  const content = R.prop('content', state);
  const currentUrl = R.path(['status', 'currentUrl'], state);

  if (!currentUrl) {
    return null;
  }

  const metaTitle = R.path(['metaInformation', 'metaTitle'], content);
  const name = metaTitle ? metaTitle.split('|')[0].trim() : '';
  const description = R.path(['metaInformation', 'metaDescription'], content);

  return createWebPageSchema({
    url: currentUrl,
    name: name || undefined,
    description: description || undefined,
  });
};

const StandardLayout = ({
  cookieContent,
  children,
  basketLink,
  isServedFromLegacySite,
  showModal,
  hideModal,
  basketQuantity,
  socialHeading,
  newsletter,
  search,
  presentationLocale,
  isOneTrustEnabled,
  showNewsletter,
  skipButtonText,
  mikMakConfig,
  webPageSchema,
}) => {
  useMikMakAdobeTracking();
  useMikMakGaTracking();
  // Page view tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const serverTrackingData =
      readJSONFromScriptTag({ id: 'serverTrackingData' }) || {};

    const clientEnv = readJSONFromScriptTag({ id: 'clientEnv' }) || {};

    // Get previous location from sessionStorage
    const prevPageLocation = sessionStorage.getItem('prevPageLocation') || '';

    // Save current location as previous for next page
    sessionStorage.setItem('prevPageLocation', window.location.href);

    trackPageView({
      window,
      serverTrackingData: {
        ...serverTrackingData,
      },
      environment: clientEnv.ENVIRONMENT || 'staging',
      prevPageLocation,
      userConsent: clientEnv.USER_CONSENT || '',
    });
  }, []);

  return (
    <div className={presentationLocale}>
      <a href="#content" className="skip-link">
        {skipButtonText || 'Skip to content'}
      </a>
      <HeaderTwoRow
        logoSrc="/ccx-files/assets/logo.png"
        logoAlt="Rimmel London home"
        leftChevronIconSrc="/ccx-files/assets/arrow-left.svg"
        rightChevronIconSrc="/ccx-files/assets/arrow-right.svg"
        closeIconSrc="/ccx-files/assets/cross.svg"
        hamburgerIconSrc="/ccx-files/assets/hamburger.svg"
        logoPosition="center"
        themeName="filled"
        navType="featuredProducts"
        showSearch
        showBasket
        showMiniCart={false}
        searchIconSrc="/ccx-files/assets/search.svg"
        miniCartIconSrc="/ccx-files/assets/basket.svg"
        basketLink={basketLink}
        onMouseEnter={showModal}
        onMouseLeave={hideModal}
        cartQuantity={basketQuantity}
        miniCart={() => <BasketModalComponent />}
        logoWidth={300}
        logoHeight={49}
      />

      <Search
        appElement="#app"
        searchIconSrc="/ccx-files/assets/search-simple.svg"
        closeIconSrc="/ccx-files/assets/cancel-simple.svg"
        clearSearchIconSrc="/ccx-files/assets/erase-simple.svg"
        searchURL={R.prop('url')(search)}
        locale={R.prop('locale')(search)}
        presentationLocale={presentationLocale}
        searchPath="/search/"
        searchFieldName="search"
        searchButtonHidden
        isServedFromLegacySite={isServedFromLegacySite}
      />
      <div id="content">{children}</div>

      {!!webPageSchema && (
        <>
          {/* eslint-disable react/no-danger */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(webPageSchema),
            }}
          />
          {/* eslint-enable react/no-danger */}
        </>
      )}

      <div className="social__pattern-background">
        <div className="grid-container">
          <div className="grid-x grid-margin-x">
            <div className="cell medium-8">
              <Social
                socialIconSrc={{
                  facebook: '/ccx-files/assets/facebook-white.svg',
                  instagram: '/ccx-files/assets/instagram-white.svg',
                  tiktok: '/ccx-files/assets/tiktok-white.svg',
                  pinterest: '/ccx-files/assets/pinterest-white.svg',
                  twitter: '/ccx-files/assets/twitter-white.svg',
                  youtube: '/ccx-files/assets/youtube-white.svg',
                  tumblr: '/ccx-files/assets/tumblr-white.svg',
                  vk: '/ccx-files/assets/vk.svg',
                }}
                heading={socialHeading}
                linkTarget="_blank"
              />
            </div>

            <OptionalSection
              renderCondition={showNewsletter}
              section={() => (
                <div className="cell medium-8">
                  <div className="newsletter-custom">
                    <Newsletter
                      buttonType="icon"
                      buttonIconSrc="/ccx-files/assets/chevron-white-right.svg"
                    />
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <FooterTwoRow
        isServedFromLegacySite={isServedFromLegacySite}
        minusIconSrc="/ccx-files/assets/arrow-left.svg"
        plusIconSrc="/ccx-files/assets/arrow-left.svg"
        countrySelector={{
          visible: true,
          beforeCopyright: true,
          showToggleIcon: false,
        }}
        headingLevel={2}
        navigationLinkIconHeight={15}
        navigationLinkIconWidth={15}
      />

      <OptionalSection
        renderCondition={!isOneTrustEnabled}
        section={() => (
          <Cookie
            position="bottom"
            content={cookieContent}
            closeIconSrc="/ccx-files/assets/cross-white.svg"
          />
        )}
      />
      {mikMakConfig.enabled && <MikMakScript appId={mikMakConfig.appId} />}
    </div>
  );
};

StandardLayout.propTypes = {
  cookieContent: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    dismissText: PropTypes.string,
  }).isRequired,
  children: PropTypes.node,
  basketLink: PropTypes.string,
  showModal: PropTypes.func,
  hideModal: PropTypes.func,
  basketQuantity: PropTypes.number,
  isServedFromLegacySite: PropTypes.bool,
  socialHeading: PropTypes.string,
  search: PropTypes.shape({
    url: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    contentLabel: PropTypes.string,
    placeholderText: PropTypes.string,
    clearSearchTermHistoryButtonText: PropTypes.string,
    popularSearches: PropTypes.array,
  }),
  newsletter: PropTypes.objectOf(PropTypes.any),
  skipButtonText: PropTypes.string,
  presentationLocale: PropTypes.string,
  isOneTrustEnabled: PropTypes.bool,
  showNewsletter: PropTypes.bool.isRequired,
  mikMakConfig: PropTypes.shape({
    appId: PropTypes.string,
    wtbId: PropTypes.string,
    enabled: PropTypes.bool,
  }),
  webPageSchema: PropTypes.object,
};

const mapStateToProps = (state) => ({
  basketQuantity: getBasketQuantity(state),
  basketLink: R.path(['content', 'navigation', 'basketLink'], state),
  cookieContent: R.path(['content', 'cookie'], state),
  isServedFromLegacySite: R.path(['status', 'isServedFromLegacySite'], state),
  search: R.path(['content', 'search'], state),
  newsletter: R.path(['content', 'newsletter'], state),
  presentationLocale: R.prop('presentationLocale', state),
  isOneTrustEnabled: R.path(['content', 'isOneTrustEnabled'], state),
  showNewsletter: R.path(['content', 'newsletter', 'submitButtonText'], state),
  skipButtonText: R.path(['content', 'header', 'skipButtonText'], state),
  mikMakConfig: R.path(['content', 'mikmak'], state),
  webPageSchema: buildWebPageSchemaForState(state),
});

const mapDispatchToProps = (dispatch) => ({
  showModal: () => dispatch(showModalAction()),
  hideModal: () => dispatch(hideModalAction()),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { omitWebPageSchema, ...restOwn } = ownProps;

  return {
    ...restOwn,
    ...stateProps,
    ...dispatchProps,
    webPageSchema: omitWebPageSchema ? null : stateProps.webPageSchema,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(StandardLayout);
